// Selection identity is distinct from the calculator's legacy repeat-setting
// key. Never use a price range as a design/SR identity.
export function optimizerFabricIdentity(row, getFabricKey) {
  const fabricKey = getFabricKey(row);
  if (!fabricKey) return "";
  return JSON.stringify([
    String(row.fabricBrandId || row.brandId || String(row.fabricBrandName || row.brandName || "").toLowerCase()).trim(),
    String(row.catalogueId || row.fabricCatalogueId || String(row.catalogueName || "").toLowerCase()).trim(),
    fabricKey,
  ]);
}

export function getOptimizerFabricOptions(rows, getFabricKey, getFabricLabel) {
  const options = new Map();
  for (const row of rows || []) {
    const key = optimizerFabricIdentity(row, getFabricKey);
    if (!key || options.has(key)) continue;
    options.set(key, { key, fabricKey: getFabricKey(row), label: getFabricLabel(row),
      brand: row.fabricBrandName || row.brandName || "" });
  }
  return [...options.values()];
}

export function selectOptimizerSource(selected, live, useLive) {
  return useLive && live && selected?.id === live.id ? live : selected;
}

export function buildOptimizerDemandInput({ quote, selectedKey, getFabricKey, buildPlan, getProductRules, getRepeatForRow }) {
  const rows = (quote?.rows || []).filter(row => optimizerFabricIdentity(row, getFabricKey) === selectedKey);
  const repeatSettings = quote?.repeatSettings || {};
  const plan = buildPlan(rows, repeatSettings, {
    enableReuse: quote?.useHalfPieceReuse !== false,
    debugTag: "optimizer", getProductRules,
    quoteFullnessOverridesForCalculation: quote?.quoteFullnessOverrides || {},
    quoteConstructionSettingsForCalculation: quote || {},
  });
  const rowDemands = (plan.rowPlans || []).filter(p => p && Number(p.newFullCuts) > 0).map(p => {
    const row = rows[p.rowIndex] || {};
    const repeat = getRepeatForRow?.(row, repeatSettings) || row.repeatSettings || {};
    const rules = getProductRules?.(row) || {};
    return { length: Number(p.cutLengthM), qty: Number(p.newFullCuts), rowId: row.id || row.rowId || '', fabricIdentity: selectedKey,
      matchingGroupId: String(row.patternMatchingGroupId || selectedKey),
      patternReferenceId: String(row.patternReferenceId || row.patternMatchingGroupId || selectedKey),
      patternOrientation: rules.fabricRules?.orientation || rules.orientation || 'standard',
      fabricWidthIn: Number(p.fabricWidthIn || row.fabricWidthIn || 0),
      horizontalRepeatInch: repeat.horizontalEnabled === true ? Number(repeat.horizontalRepeatInch || 0) : 0,
      verticalMatchingRequired: repeat.verticalEnabled === true && Number(repeat.verticalRepeatInch) > 0,
      verticalRepeatMeters: repeat.verticalEnabled === true ? Number(repeat.verticalRepeatInch || 0) * 0.0254 : 0 };
  });
  const demands = rowDemands.length ? rowDemands : plan.demands || [];
  const excludedRows = plan.skippedReasons || [];
  const selectedIds = new Set(rows);
  const otherRows = (quote?.rows || []).filter(row => !selectedIds.has(row)).map(row => ({
    rowId: row.id || row.rowId || "", reason: "Different or missing selected fabric identity",
    fabricKey: optimizerFabricIdentity(row, getFabricKey),
  }));
  const fabricKey = rows.length ? getFabricKey(rows[0]) : "";
  const summary = plan.fabricSummaryByKey?.[fabricKey] || {};
  let failureReason = "";
  if (!rows.length) failureReason = "The selected fabric no longer matches any rows. Select the fabric again.";
  else if (!demands.length) {
    const reasons = new Set(excludedRows.map(row => row.reason));
    failureReason = [...reasons].some(reason => /horizontal pattern matching requires manual review/.test(reason))
      ? "Horizontal pattern matching needs manual cutting review for this fabric."
      : [...reasons].some(reason => /not enabled|not a curtain or roman/.test(reason))
      ? "This product has fabric requirements but does not have an enabled, supported cutting-panel calculation."
      : "Cutting panels could not be built. Check the selected rows' dimensions, quantity and fabric settings.";
  }
  const manualWarnings = Number(plan.audit?.totalHalfPiecesReused || 0) > 0
    ? ['Half-piece reuse is retained. Verify the reused pieces\' motif, direction and matching reference manually before cutting.'] : [];
  return { rows, repeatSettings, plan, demands, fabricKey, manualWarnings,
    failureReason, trace: { quoteId: quote?.quoteId || quote?.id || "", selectedFabricKey: selectedKey,
      rawRowCount: quote?.rows?.length || 0, effectiveRowCount: rows.length,
      matchingRowIds: rows.map(row => row.id || row.rowId),
      matchingProductTypes: rows.map(row => row.productType || row.productName),
      panelDemandCount: demands.length,
      halfPiecesReused: plan.audit?.totalHalfPiecesReused || 0,
      rowPlans: (plan.rowPlans || []).filter(Boolean).map(row => ({ rowId: row.rowId, rawPanels: row.rawPanels,
        wholePanels: row.wholePanels, halfStepPanels: row.halfStepPanels, newFabricNeededMeters: row.newFabricNeededMeters })),
      eligibleFabricRowCount: plan.rowPlans?.filter(Boolean).length || 0,
      workshopFabricToOrderMetersRaw: summary.newFabricNeededMeters ?? null,
      fabricSummaryMetersRaw: summary.cutFabricMeters ?? null,
      // Billing is intentionally not inferred from physical cutting demand.
      customerBillableMetersRaw: null,
      totalRequiredPanels: demands.reduce((sum, demand) => sum + Number(demand.qty || 0), 0),
      totalCutLengthMeters: demands.reduce((sum, demand) => sum + Number(demand.qty || 0) * Number(demand.length || 0), 0),
      excludedRows: [...excludedRows, ...otherRows],
      exclusionReasons: [...new Set([...excludedRows, ...otherRows].map(row => row.reason))],
      failureReason, repeatSettings, halfPieceReuseEnabled: quote?.useHalfPieceReuse !== false } };
}
